import { t } from '../../../i18n'
import {
  EmissionFragment,
  EmissionType,
  ProjectFragment,
} from '../../../generated/graphql'
import { groupBy, sortByProperty } from '../../../util/array-utility'
import { FullFormatOptions } from '../../../util/formatters'
import { SelectedVersion } from '../../layout/VersionsSelector'
import { NO_MATCHING_RESOURCE_SYMBOL } from '../../common/constants'

export type EmissionRow = {
  emissionType: EmissionType
  emissions: EmissionWithVersionId[]
}
export type GroupedEmission = {
  buildingName: string
  rows: EmissionRow[]
}

export type EmissionWithVersionId = EmissionFragment & { versionId: string }

type EmissionData = {
  versionId: string
  buildingName: string
  emissions: EmissionFragment[]
}

export enum TableType {
  OneTime = 'ONE_TIME',
  Recurring = 'RECURRING',
}

const mapEmissionData = (emissionData: EmissionData) =>
  emissionData.emissions.map((emission) => ({
    versionId: emissionData.versionId,
    ...emission,
  }))

const filterByTableType = (row: EmissionRow, tableType: TableType): boolean => {
  if (
    tableType === TableType.OneTime &&
    row.emissionType !== EmissionType.Recurring
  ) {
    return true
  }
  if (
    tableType === TableType.Recurring &&
    row.emissionType === EmissionType.Recurring
  ) {
    return true
  }
  return false
}

export const groupEmissions = (
  project: ProjectFragment,
  type: TableType,
): GroupedEmission[] => {
  const versionBuildings = project.versions.flatMap((version) =>
    version.buildings.map((building) => ({
      versionId: version.id,
      buildingName: building.name,
      emissions: building.emissions,
    })),
  )
  const groupedBuildings = groupBy(
    versionBuildings,
    (building) => building.buildingName,
  )

  const mapRows = (
    buildingRecord: Record<string, EmissionWithVersionId[]>,
  ): EmissionRow[] =>
    Object.entries(buildingRecord)
      .map(
        ([emissionType, values]) =>
          ({ emissionType, emissions: values } as EmissionRow),
      )
      .filter((row) => filterByTableType(row, type))

  return Object.entries(groupedBuildings).map(
    ([buildingName, emissionData]) => ({
      buildingName,
      rows: mapRows(
        groupBy(
          emissionData.flatMap(mapEmissionData),
          (entry) => entry.emissionType,
        ),
      ),
    }),
  )
}

export const checkIfVersionHasBuilding = (
  versionId: string,
  versionIdsToBuildingNames: Record<string, string[]>,
  buildingName: string,
): boolean => {
  const buildingNames = versionIdsToBuildingNames[versionId]
  return buildingNames && buildingNames.includes(buildingName)
}

export const getVersionValue = (
  version: SelectedVersion,
  emissionRow: EmissionRow,
  formatter: (value: number, options: FullFormatOptions) => string,
  buildingExists: boolean,
): string => {
  if (!buildingExists) {
    return NO_MATCHING_RESOURCE_SYMBOL
  }
  const versionEmission = emissionRow.emissions.find(
    (item) => item.versionId === version.id,
  )
  const value = versionEmission ? versionEmission.value : 0

  const formattedValue =
    emissionRow.emissionType !== EmissionType.Handprint
      ? formatter(value, { omitUnit: true })
      : `-${formatter(value, { omitUnit: true })}`

  return formattedValue
}

export const getPrefillData = (
  versions: SelectedVersion[],
  row: EmissionRow,
): EmissionWithVersionId => {
  const defaultVersion = versions.find((version) => version.isDefault)
  const emission =
    row.emissions.find((e) => e.versionId === defaultVersion?.id) ??
    row.emissions.find(Boolean)

  if (!emission) {
    throw new Error('No emission found as a base for prefill')
  }

  return emission
}

export const sortGroupedEmissions = (
  groupedEmissions: GroupedEmission[],
): GroupedEmission[] =>
  sortByProperty(groupedEmissions, 'buildingName').map((group) => ({
    ...group,
    rows: group.rows.sort((c, d) =>
      t(`emissionType.${c.emissionType}`).localeCompare(
        t(`emissionType.${d.emissionType}`),
      ),
    ),
  }))
